import React, { useState, useRef } from 'react';
import {
    Form,
    Button,
    Row,
    Col,
    FloatingLabel,
    Image,
    Spinner,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useAuth } from '@/shared/contexts/AuthContext';
import { submitFeedback } from '@/shared/services/feedbackApi';
import Captcha from '@/components/common/Captcha';

// Category options
const CATEGORIES = [
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'feedback', label: 'General Feedback' },
];

// Page/Feature options: RILE Connect pages
const PAGE_FEATURES = [
    { value: '', label: 'Select a page or feature...' },
    { value: 'Home Page', label: 'Home Page' },
    // Labels track the current page names (#242: Directory→Ecosystem,
    // Ecosystem Analytics→Insights). The `value`s are intentionally left
    // unchanged: they are persisted with submitted feedback, and rewriting
    // them would split reporting against historical rows.
    { value: 'Directory', label: 'Ecosystem' },
    { value: 'Ecosystem Analytics', label: 'Insights' },
    { value: 'Discover', label: 'Discover' },
    { value: 'Opportunities', label: 'Opportunities' },
    { value: 'AI Assistant', label: 'AI Assistant' },
    { value: 'Organization Detail', label: 'Organization Detail' },
    { value: 'Profile / Account', label: 'Profile / Account' },
    { value: 'Dashboard', label: 'Dashboard' },
    { value: 'Onboarding', label: 'Onboarding' },
    { value: 'Login / Signup', label: 'Login / Signup' },
    { value: 'Navigation', label: 'Navigation' },
    { value: 'Other', label: 'Other' },
];

export default function FeedbackForm({ onSuccess = null }) {
    const { user } = useAuth();
    const fileInputRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        category: '',
        description: '',
        pageFeature: '',
        email: user?.email || '',
        contactRequested: false,
        screenshot: null,
    });

    const [touched, setTouched] = useState({
        category: false,
        description: false,
        email: false,
    });

    const [errors, setErrors] = useState({});
    const [captchaToken, setCaptchaToken] = useState(null);
    const captchaRef = useRef(null);

    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [screenshotPreview, setScreenshotPreview] = useState(null);

    const validate = (values) => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!values.category) {
            newErrors.category = 'Please select a category.';
        }
        if (!values.description.trim()) {
            newErrors.description = 'Description is required.';
        } else if (values.description.length < 10) {
            newErrors.description =
                'Description must be at least 10 characters.';
        }
        if (!values.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!emailRegex.test(values.email)) {
            newErrors.email = 'Please provide a valid email.';
        }

        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        const updatedFormData = { ...formData, [name]: newValue };
        setFormData(updatedFormData);
        setErrors(validate(updatedFormData));
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        setErrors(validate(formData));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setSubmitError('Screenshot must be less than 5MB');
            return;
        }

        const allowedTypes = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
        ];
        if (!allowedTypes.includes(file.type)) {
            setSubmitError(
                'Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.'
            );
            return;
        }

        if (screenshotPreview) {
            URL.revokeObjectURL(screenshotPreview);
        }
        setFormData((prev) => ({ ...prev, screenshot: file }));
        setScreenshotPreview(URL.createObjectURL(file));
        setSubmitError('');
    };

    const handleRemoveScreenshot = () => {
        if (screenshotPreview) {
            URL.revokeObjectURL(screenshotPreview);
        }
        setFormData((prev) => ({ ...prev, screenshot: null }));
        setScreenshotPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const newErrors = validate(formData);
        setErrors(newErrors);
        setTouched({ category: true, description: true, email: true });

        if (Object.keys(newErrors).length > 0) {
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setSubmitError('');

        try {
            await submitFeedback({
                ...formData,
                userId: user?.uid,
                isLoggedIn: !!user,
                captchaToken,
            });

            setSubmitSuccess(true);

            if (onSuccess) {
                setTimeout(() => onSuccess(), 2000);
            }
        } catch (err) {
            setSubmitError(
                err.message || 'Failed to submit feedback. Please try again.'
            );
            captchaRef.current?.reset();
            setCaptchaToken(null);
        } finally {
            setLoading(false);
        }
    };

    // Success state
    if (submitSuccess) {
        return (
            <div className="text-center py-5">
                <h2 className="text-primary mb-3">Thank You!</h2>
                <p className="text-muted">
                    Your feedback has been submitted successfully. We appreciate
                    you taking the time to help us improve RILE Connect.
                </p>
            </div>
        );
    }

    return (
        <Form noValidate onSubmit={handleSubmit}>
            {/* Header */}
            <h2 className="text-primary mb-2">Send Feedback</h2>
            <p className="text-muted mb-4">
                Help us improve RILE Connect by sharing your thoughts, reporting
                bugs, or suggesting new features.
            </p>

            {/* Error Message */}
            {submitError && (
                <div className="alert alert-danger" role="alert">
                    {submitError}
                </div>
            )}

            {/* Category */}
            <Form.Group className="mb-3">
                <FloatingLabel controlId="feedbackCategory" label="Category *">
                    <Form.Select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.category && !!errors.category}
                        isValid={
                            touched.category &&
                            !errors.category &&
                            formData.category !== ''
                        }
                        disabled={loading}
                    >
                        <option value="">Select a category...</option>
                        {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                                {cat.label}
                            </option>
                        ))}
                    </Form.Select>
                </FloatingLabel>
                {(touched.category || validated) && errors.category && (
                    <div className="invalid-feedback d-block">
                        {errors.category}
                    </div>
                )}
            </Form.Group>

            {/* Page/Feature */}
            <Form.Group className="mb-3">
                <FloatingLabel
                    controlId="feedbackPageFeature"
                    label="Page / Feature"
                >
                    <Form.Select
                        name="pageFeature"
                        value={formData.pageFeature}
                        onChange={handleChange}
                        disabled={loading}
                    >
                        {PAGE_FEATURES.map((pf) => (
                            <option key={pf.value} value={pf.value}>
                                {pf.label}
                            </option>
                        ))}
                    </Form.Select>
                </FloatingLabel>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-3">
                <Form.Control
                    as="textarea"
                    name="description"
                    placeholder="Please describe your feedback in detail... (minimum 10 characters)"
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.description && !!errors.description}
                    isValid={
                        touched.description &&
                        !errors.description &&
                        formData.description.trim() !== ''
                    }
                    disabled={loading}
                    className="feedback-textarea"
                />
                {(touched.description || validated) && errors.description && (
                    <div className="invalid-feedback d-block">
                        {errors.description}
                    </div>
                )}
            </Form.Group>

            {/* Screenshot Upload */}
            <Form.Group className="mb-3">
                <Form.Label className="text-muted small mb-1">
                    Screenshot (Optional)
                </Form.Label>
                <Form.Control
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileChange}
                    disabled={loading}
                />
                <Form.Text className="text-muted">
                    Max 5MB. Accepted: JPEG, PNG, GIF, WebP
                </Form.Text>
                {screenshotPreview && (
                    <div className="mt-2 d-flex align-items-start gap-2">
                        <Image
                            src={screenshotPreview}
                            alt="Screenshot preview"
                            className="feedback-screenshot-preview rounded"
                        />
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleRemoveScreenshot}
                            aria-label="Remove screenshot"
                        >
                            Remove
                        </Button>
                    </div>
                )}
            </Form.Group>

            {/* Email */}
            <Form.Group className="mb-3">
                <FloatingLabel controlId="feedbackEmail" label="Email *">
                    <Form.Control
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                        isValid={
                            touched.email &&
                            !errors.email &&
                            formData.email.trim() !== ''
                        }
                        disabled={loading}
                    />
                </FloatingLabel>
                {(touched.email || validated) && errors.email && (
                    <div className="invalid-feedback d-block">
                        {errors.email}
                    </div>
                )}
            </Form.Group>

            {/* Contact Me */}
            <Form.Group className="mb-4">
                <Form.Check
                    type="checkbox"
                    id="contactRequested"
                    name="contactRequested"
                    checked={formData.contactRequested}
                    onChange={handleChange}
                    label="I'd like to be contacted about this feedback"
                    disabled={loading}
                />
            </Form.Group>

            {/* Captcha */}
            <Row className="mb-4 justify-content-center">
                <Col xs="auto">
                    <Captcha
                        ref={captchaRef}
                        onVerify={setCaptchaToken}
                        size="normal"
                        theme="light"
                        onExpired={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                    />
                </Col>
            </Row>

            {/* Submit */}
            <div className="d-grid">
                <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="rounded-pill"
                    disabled={loading || !captchaToken}
                >
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Submitting...
                        </>
                    ) : (
                        'Submit Feedback'
                    )}
                </Button>
            </div>
        </Form>
    );
}

FeedbackForm.propTypes = {
    onSuccess: PropTypes.func,
};
